import numeral from 'numeral'

import { SharePriceRecordedAsType, TradeIntentSizeType, TradeIntentView } from 'graphql/bindings/AdminBindings'

export const DEFAULT_FEE = 0.05

const isNumber = (val: any): val is number => {
  return typeof val === 'number'
}

const NOT_AVAILABLE_LABEL = 'N/A'
const VALUATION_LABEL = 'val'
const SHARES_LABEL = 'shares'
const PRICING_LABELS = {
  [SharePriceRecordedAsType.Gross]: 'gross pps',
  [SharePriceRecordedAsType.Ntb]: 'NTB pps',
  [SharePriceRecordedAsType.Nts]: 'NTS pps',
}

const withLabelSuffix = (base: string, suffix: string): string => `${base} ${suffix}`

const isRange = (min?: number, max?: number): boolean => {
  if (!isNumber(min) || !isNumber(max)) return false
  return min !== max
}

const formatRange = (min: string, max: string): string => `${min} - ${max}`

const formatValuation = (val?: number): string =>
  isNumber(val)
    ? numeral(val)
        .format('($0[.]000a)')
        .toUpperCase()
    : ''

export const formatUSD = (val?: number) =>
  isNumber(val)
    ? numeral(val)
        .format('($0[.]00a)')
        .toUpperCase()
    : ''

export const formatCount = (val?: number) => (isNumber(val) ? numeral(val).format('0,0') : '')

export const toNTB = (gross: number, fee?: number): number => gross * (1 + (fee ?? 0))
export const toNTS = (gross: number, fee?: number): number => gross * (1 - (fee ?? 0))

const convertFromGross = (gross: number, type: SharePriceRecordedAsType, fee?: number): number => {
  switch (type) {
    case SharePriceRecordedAsType.Ntb:
      return toNTB(gross, fee)
    case SharePriceRecordedAsType.Nts:
      return toNTS(gross, fee)
    case SharePriceRecordedAsType.Gross:
      return gross
    default:
      throw new Error('Encountered unknown SharePriceRecordedAsType.')
  }
}

const formatPriceAsRecordedWithLabel = (
  gross: number,
  type: SharePriceRecordedAsType = SharePriceRecordedAsType.Gross,
  fee?: number
): string => {
  return `${formatUSD(convertFromGross(gross, type, fee))} ${PRICING_LABELS[type]}`
}

const formatValuationWithLabel = (val: number): string => `${formatValuation(val)} ${VALUATION_LABEL}`

export interface FormatPriceOrValuationOptions {
  pricingType?: string | null
  sharePrice?: number
  valuation?: number
  fee?: number
  sharePriceRecordedAs?: SharePriceRecordedAsType
}

export const formatPriceOrValuation = ({
  pricingType,
  sharePrice,
  valuation,
  fee,
  sharePriceRecordedAs,
}: FormatPriceOrValuationOptions): string => {
  switch (pricingType) {
    case 'share_price': {
      if (sharePrice) {
        return formatPriceAsRecordedWithLabel(sharePrice, sharePriceRecordedAs, fee)
      }
      return NOT_AVAILABLE_LABEL
    }
    case 'valuation': {
      return valuation ? formatValuationWithLabel(valuation) : NOT_AVAILABLE_LABEL
    }
    default: {
      if (sharePrice) {
        return formatPriceAsRecordedWithLabel(sharePrice, sharePriceRecordedAs, fee)
      } else if (valuation) {
        return valuation ? formatValuationWithLabel(valuation) : NOT_AVAILABLE_LABEL
      }
      return NOT_AVAILABLE_LABEL
    }
  }
}

type PriceOrValuationFromTradeIntent = Pick<
  TradeIntentView,
  'pricing_type' | 'share_price' | 'valuation' | 'fee' | 'share_price_recorded_as'
>

export const tradeIntentPriceOrValuation = ({
  pricing_type,
  share_price,
  valuation,
  fee,
  share_price_recorded_as,
}: PriceOrValuationFromTradeIntent): string =>
  formatPriceOrValuation({
    pricingType: pricing_type,
    sharePrice: share_price,
    valuation,
    fee,
    sharePriceRecordedAs: share_price_recorded_as,
  })

const impliedSharePriceFromValuation = (valuation: number, outstandingShares: number): number =>
  valuation / outstandingShares

const impliedSharesFromDollars = (dollars: number, sharePrice: number): number => dollars / sharePrice

const impliedValuationFromSharePrice = (sharePrice: number, outstandingShares: number): number =>
  sharePrice * outstandingShares

const impliedDollarsFromSharePrice = (sharePrice: number, shares: number): number => sharePrice * shares

export interface AltPriceOrValuationOptions {
  pricingType?: string
  sharePrice?: number
  valuation?: number
  outstandingShares?: number
}

export const altPriceOrValuation = ({
  pricingType,
  sharePrice,
  valuation,
  outstandingShares,
}: AltPriceOrValuationOptions) => {
  switch (pricingType) {
    case 'share_price': {
      if (isNumber(valuation)) {
        return formatValuationWithLabel(valuation)
      } else if (isNumber(sharePrice) && isNumber(outstandingShares)) {
        return formatValuationWithLabel(impliedValuationFromSharePrice(sharePrice, outstandingShares))
      }
      return NOT_AVAILABLE_LABEL
    }
    case 'valuation': {
      if (isNumber(sharePrice)) {
        return withLabelSuffix(formatUSD(sharePrice), PRICING_LABELS[SharePriceRecordedAsType.Gross])
      } else if (isNumber(valuation) && isNumber(outstandingShares)) {
        return withLabelSuffix(
          formatUSD(impliedSharePriceFromValuation(valuation, outstandingShares)),
          PRICING_LABELS[SharePriceRecordedAsType.Gross]
        )
      }
      return NOT_AVAILABLE_LABEL
    }
    default: {
      return NOT_AVAILABLE_LABEL
    }
  }
}

type AltPriceOrValuationFromTradeIntent = Pick<TradeIntentView, 'pricing_type' | 'share_price' | 'valuation'>

export const altTradeIntentPriceOrValuation = (
  { pricing_type, share_price, valuation }: AltPriceOrValuationFromTradeIntent,
  outstandingShares: number
): string => {
  return altPriceOrValuation({
    pricingType: pricing_type ?? undefined,
    sharePrice: share_price,
    valuation,
    outstandingShares,
  })
}

export interface FormatSizeOrSharesOptions {
  minDollarAmount?: number
  maxDollarAmount?: number
  minNumShares?: number
  maxNumShares?: number
  sizeType?: TradeIntentSizeType | null
}

export const formatSizeOrShares = ({
  minDollarAmount,
  maxDollarAmount,
  minNumShares,
  maxNumShares,
  sizeType,
}: FormatSizeOrSharesOptions): string => {
  switch (sizeType) {
    case TradeIntentSizeType.Shares: {
      if (isNumber(minNumShares) && isNumber(maxNumShares)) {
        return isRange(minNumShares, maxNumShares)
          ? withLabelSuffix(formatRange(formatCount(minNumShares), formatCount(maxNumShares)), SHARES_LABEL)
          : withLabelSuffix(formatCount(minNumShares), SHARES_LABEL)
      }
      return NOT_AVAILABLE_LABEL
    }
    case TradeIntentSizeType.Dollars: {
      if (isNumber(minDollarAmount) && isNumber(maxDollarAmount)) {
        return isRange(minDollarAmount, maxDollarAmount)
          ? formatRange(formatUSD(minDollarAmount), formatUSD(maxDollarAmount))
          : formatUSD(minDollarAmount)
      }
      return NOT_AVAILABLE_LABEL
    }
    default: {
      return NOT_AVAILABLE_LABEL
    }
  }
}

type SizeOrSharesFromTradeIntent = Pick<
  TradeIntentView,
  'min_dollar_amount' | 'max_dollar_amount' | 'min_num_shares' | 'max_num_shares' | 'size_type'
>

export const tradeIntentSizeOrShares = ({
  min_dollar_amount,
  max_dollar_amount,
  min_num_shares,
  max_num_shares,
  size_type,
}: SizeOrSharesFromTradeIntent): string =>
  formatSizeOrShares({
    minDollarAmount: min_dollar_amount,
    maxDollarAmount: max_dollar_amount,
    minNumShares: min_num_shares,
    maxNumShares: max_num_shares,
    sizeType: size_type,
  })

export interface AltSizeOrSharesOptions {
  minDollarAmount?: number
  maxDollarAmount?: number
  minNumShares?: number
  maxNumShares?: number
  sizeType?: TradeIntentSizeType | null
  sharePrice?: number
  valuation?: number
  outstandingShares?: number
}

const priceWithImpliedFallback = (
  sharePrice?: number,
  valuation?: number,
  outstandingShares?: number
): number | null => {
  if (isNumber(sharePrice)) return sharePrice
  if (isNumber(valuation) && isNumber(outstandingShares)) {
    return impliedSharePriceFromValuation(valuation, outstandingShares)
  }
  return null
}

export const altSizeOrShares = ({
  minDollarAmount,
  maxDollarAmount,
  minNumShares,
  maxNumShares,
  sizeType,
  sharePrice,
  valuation,
  outstandingShares,
}: AltSizeOrSharesOptions): string => {
  const impliedPrice = priceWithImpliedFallback(sharePrice, valuation, outstandingShares)
  if (impliedPrice === null) return NOT_AVAILABLE_LABEL

  switch (sizeType) {
    case 'shares': {
      if (isNumber(minDollarAmount) && isNumber(maxDollarAmount)) {
        return isRange(minDollarAmount, maxDollarAmount)
          ? formatRange(formatUSD(minDollarAmount), formatUSD(maxDollarAmount))
          : formatUSD(minDollarAmount)
      } else if (isNumber(minNumShares) && isNumber(maxNumShares)) {
        const impliedMinDollars = impliedDollarsFromSharePrice(impliedPrice, minNumShares)
        const impliedMaxDollars = impliedDollarsFromSharePrice(impliedPrice, maxNumShares)
        return isRange(minNumShares, maxNumShares)
          ? formatRange(formatUSD(impliedMinDollars), formatUSD(impliedMaxDollars))
          : formatUSD(impliedMinDollars)
      }
      return NOT_AVAILABLE_LABEL
    }
    case 'dollars': {
      if (isNumber(minNumShares) && isNumber(maxNumShares)) {
        return isRange(minNumShares, maxNumShares)
          ? withLabelSuffix(formatRange(formatCount(minNumShares), formatCount(maxNumShares)), SHARES_LABEL)
          : withLabelSuffix(formatCount(minNumShares), SHARES_LABEL)
      } else if (isNumber(minDollarAmount) && isNumber(maxDollarAmount)) {
        const impliedMinShares = impliedSharesFromDollars(minDollarAmount, impliedPrice)
        const impliedMaxShares = impliedSharesFromDollars(maxDollarAmount, impliedPrice)
        return isRange(minDollarAmount, maxDollarAmount)
          ? withLabelSuffix(formatRange(formatCount(impliedMinShares), formatCount(impliedMaxShares)), SHARES_LABEL)
          : withLabelSuffix(formatCount(impliedMinShares), SHARES_LABEL)
      }
      return NOT_AVAILABLE_LABEL
    }
    default: {
      return NOT_AVAILABLE_LABEL
    }
  }
}

type AltSizeOrSharesFromTradeIntent = Pick<
  TradeIntentView,
  | 'size_type'
  | 'share_price'
  | 'valuation'
  | 'min_num_shares'
  | 'max_num_shares'
  | 'min_dollar_amount'
  | 'max_dollar_amount'
>

export const altTradeIntentSizeOrShares = (
  {
    size_type,
    share_price,
    valuation,
    min_num_shares,
    max_num_shares,
    min_dollar_amount,
    max_dollar_amount,
  }: AltSizeOrSharesFromTradeIntent,
  outstandingShares: number
): string => {
  return altSizeOrShares({
    minDollarAmount: min_dollar_amount,
    maxDollarAmount: max_dollar_amount,
    minNumShares: min_num_shares,
    maxNumShares: max_num_shares,
    sizeType: size_type,
    sharePrice: share_price,
    valuation,
    outstandingShares,
  })
}
